import { useRef, useState } from "react"
import { Button, Flex, Modal, Spin } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"
import { spacings } from "themes/variables"
import { Text } from "ui/Typography"
import PropTypes from "prop-types"
import ClientContactForm from "../ClientsContactWindow"
import { routes } from "router"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import useActiveGuide from "hooks/useActiveClient"

const BulkActions = ({
  clients,
  selectedRowKeys,
  setSelectedRowKeys,
  onGroupAction,
  canEditClient,
  totalClients,
  onSelectAllPages,
  allSelectedClients,
  setAllSelectedClients
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)
  const [contactType, setContactType] = useState("")
  const [isSelectingAll, setIsSelectingAll] = useState(false)
  const title = t("global.send") + " " + t(`client.${contactType}`)
  const { metadata } = useSelector((store) => store.clients)
  const { isActive: active } = useActiveGuide()
  const requestCloseHandlerRef = useRef(null)

  const selectedKeySet = new Set(selectedRowKeys.map(Number))
  const clientPool = allSelectedClients?.length > 0 ? allSelectedClients : clients
  const clientsForAction = clientPool?.filter((c) => selectedKeySet.has(Number(c.user_id)))
  const userIdsForAction = clientsForAction?.map((c) => c.user_id)

  const handleMenuClick = (type) => {
    const templates = metadata[`${type}_templates_collection`]
    setContactType(type)

    const routeMap = {
      email: routes.guideClientEmailActionPath,
      text: routes.guideClientTextActionPath,
      message: routes.guideClientMessageActionPath
    }

    const routeFn = routeMap[type] || routes.guideClientMessageActionPath

    navigate(routeFn(), { state: { clients: clientsForAction, userIds: userIdsForAction, templates } })
  }

  const isGroupDisabled = !active || !canEditClient
  const isContactDisabled = !active

  const actionButtons = [
    {
      key: "add_group",
      label: t("clients.bulk_actions.add_to_group"),
      onClick: () => onGroupAction("bulk_add", selectedRowKeys),
      disabled: isGroupDisabled
    },
    {
      key: "remove_group",
      label: t("clients.bulk_actions.remove_from_group"),
      onClick: () => onGroupAction("bulk_remove", selectedRowKeys),
      disabled: isGroupDisabled
    },
    {
      key: "email",
      label: t("clients.bulk_actions.email_selection"),
      onClick: () => handleMenuClick("email"),
      disabled: isContactDisabled
    },
    {
      key: "message",
      label: t("clients.bulk_actions.message_selection"),
      onClick: () => handleMenuClick("message"),
      disabled: isContactDisabled
    },
    {
      key: "text",
      label: t("clients.bulk_actions.text_selection"),
      onClick: () => handleMenuClick("text"),
      disabled: isContactDisabled
    }
  ]

  const handleClose = () => {
    setIsVisible(false)
    setContactType("")
  }

  const registerRequestClose = (handler) => {
    requestCloseHandlerRef.current = handler
  }

  const handleRequestClose = () => {
    if (typeof requestCloseHandlerRef.current === "function") {
      requestCloseHandlerRef.current()
      return
    }

    handleClose()
  }

  return (
    <>
      <Flex align="center" wrap="wrap" className={`py-s px-l mb-s position-relative ${styles.bulkActionsContainer}`} gap={spacings["XS"]}>
        <Text className={styles.selectedText}>{t("clients.selected", { count: selectedRowKeys.length })}</Text>
        {totalClients > selectedRowKeys.length ? (
          <Spin spinning={isSelectingAll} size="small">
            <Button
              color="primary"
              variant="outlined"
              className="px-l"
              disabled={isSelectingAll}
              onClick={() => {
                setIsSelectingAll(true)
                onSelectAllPages().finally(() => setIsSelectingAll(false))
              }}
            >
              {t("clients.select_all_records", { count: totalClients })}
            </Button>
          </Spin>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            className="px-l"
            onClick={() => {
              setSelectedRowKeys([])
              setAllSelectedClients([])
            }}
          >
            {t("clients.clear_selection")}
          </Button>
        )}
        <Flex gap={spacings["XS"]} className="ml-auto" wrap="wrap" align="flex-start">
          {actionButtons.map((btn) => (
            <Button
              key={btn.key}
              color="primary"
              variant="outlined"
              className="px-l"
              onClick={btn.disabled ? undefined : btn.onClick}
              disabled={btn.disabled}
            >
              {btn.label}
            </Button>
          ))}
          <Button
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            className={styles.closeButton}
            onClick={() => {
              setSelectedRowKeys([])
              setAllSelectedClients([])
            }}
          />
        </Flex>
      </Flex>

      <Modal
        title={title}
        width={{ xxl: 800, xl: 600, xs: 400 }}
        open={isVisible}
        footer={null}
        onCancel={handleRequestClose}
        destroyOnClose
      >
        <ClientContactForm
          handleClose={handleClose}
          type={contactType}
          clients={clientsForAction}
          userIds={userIdsForAction}
          setSelectedRowKeys={setSelectedRowKeys}
          registerRequestClose={registerRequestClose}
        />
      </Modal>
    </>
  )
}

BulkActions.propTypes = {
  clients: PropTypes.array,
  selectedRowKeys: PropTypes.array,
  setSelectedRowKeys: PropTypes.func,
  onGroupAction: PropTypes.func,
  canEditClient: PropTypes.bool,
  totalClients: PropTypes.number,
  onSelectAllPages: PropTypes.func,
  allSelectedClients: PropTypes.array,
  setAllSelectedClients: PropTypes.func
}

export default BulkActions
